import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { NumericTextInput } from '@instinet-analytics/semantic-ui-components';
import { DatePicker, timeUtils } from '@instinet-analytics/core-lib';
import AppContext from 'app/AppContext';
import { PortfolioReturnsComponentState } from 'widgets/portfolioReturns/store/component/PortfolioReturnsComponentState';
import { DateTime } from 'luxon';
import { PortfolioReturnsConfigurationState } from 'widgets/portfolioReturns/store/configuration/PortfolioReturnsConfigurationState';
import { HelpPortal } from 'utils/components/helpPortal/HelpPortal';

const { roundToDays } = timeUtils;

const today = roundToDays(new Date().getTime());

export interface PortfolioReturnsSettingsDateRangeProps {
    component: PortfolioReturnsComponentState;
    onComponentChange: (
        component: Partial<PortfolioReturnsComponentState>
    ) => void;
    days: number | undefined;
    onConfigChange: (
        component: Partial<PortfolioReturnsConfigurationState>
    ) => void;
}

export function PortfolioReturnsSettingsDateRange(
    props: PortfolioReturnsSettingsDateRangeProps
) {
    const { component, days, onComponentChange, onConfigChange } = props;
    const [daysInputValue, setDaysInputValue] = useState(days ?? 1);

    const { fromDate, toDate } = component;

    useEffect(() => {
        const effectiveDays = days ?? 1;
        if (effectiveDays !== daysInputValue) {
            setDaysInputValue(effectiveDays);
        }
    }, [days, daysInputValue, setDaysInputValue]);

    const onDaysChanged = (nextDays: number | undefined) =>
        onConfigChange({ days: nextDays });

    const onToDateChanged = (nextToDate: Date | null) => {
        const nextToDateTime = nextToDate
            ? roundToDays(nextToDate?.getTime())
            : undefined;
        onComponentChange({
            toDate:
                nextToDateTime === today
                    ? undefined
                    : nextToDateTime
                    ? new Date(nextToDateTime)
                    : undefined,
        });
        if (fromDate && nextToDate) {
            onDaysChanged(undefined);
        }
    };

    const onFromDateChanged = (nextFromDate: Date | null) => {
        const nextFromDateTime = nextFromDate
            ? roundToDays(nextFromDate?.getTime())
            : undefined;
        if (!fromDate) {
            onDaysChanged(undefined);
        }
        onComponentChange({
            fromDate: nextFromDateTime ? new Date(nextFromDateTime) : undefined,
        });
    };

    const onDaysInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        try {
            const nextValue = parseInt(event.target.value);
            onDaysChanged(nextValue);
            if (fromDate) {
                onToDateChanged(null);
            }
        } catch {
            // Ignore...
        }
    };

    const tradingDaysHelp = fromDate ? (
        <span>
            Number of trading days counting forward from the &quot;From
            Date&quot;
        </span>
    ) : (
        <span>
            Number of trading days counting back from the &quot;To Date&quot;
        </span>
    );

    const showFromRadios = !!fromDate;

    const toDateRadioChecked = days === undefined;
    const daysRadioChecked = days !== undefined;

    const onDateRadioClicked = () => {
        if (toDateRadioChecked) {
            onDaysChanged(daysInputValue);
        } else {
            onDaysChanged(undefined);
        }
    };

    const onDaysRadioClicked = () => {
        if (daysRadioChecked) {
            onDaysChanged(undefined);
        } else {
            onToDateChanged(null);
            onDaysChanged(daysInputValue);
        }
    };

    const tradingDaysLabel = fromDate ? 'Look Forward' : 'Look Back';
    const fromDateInUse = !!fromDate;
    const toDateInUse =
        toDate || // Value set
        (fromDate && !days) || // From date to today
        (!fromDate && days) || // days to today
        (!fromDate && !toDate); // 1 day to today
    const daysInUse = !(fromDateInUse && toDateInUse);

    return (
        <div className="group">
            <div className="horizontal chart-date">
                <div className={fromDate ? undefined : 'disabled-label'}>
                    From Date
                </div>
                <DateBoundaryEditor
                    className={classNames(
                        'from-date',
                        fromDateInUse && 'in-use'
                    )}
                    maxDate={
                        toDate
                            ? DateTime.fromJSDate(toDate)
                                  .plus({ day: -1 })
                                  .toJSDate()
                            : undefined
                    }
                    placeholder={'Click to set'}
                    value={fromDate ?? null}
                    onValueChanged={onFromDateChanged}
                    showClearButton={true}
                />
            </div>

            <div className={classNames('horizontal days')}>
                <div>
                    {showFromRadios && (
                        <div
                            className="ui radio checkbox from-duration-radio use-trading-days"
                            onClick={onDaysRadioClicked}
                        >
                            <input
                                className="hidden"
                                type="radio"
                                readOnly={true}
                                checked={daysRadioChecked}
                            />
                            <label>&nbsp;</label>
                        </div>
                    )}
                    <span>{tradingDaysLabel}</span>
                </div>
                <NumericTextInput
                    className={classNames('days-input', daysInUse && 'in-use')}
                    name="days"
                    value={daysInputValue.toString()}
                    min={1}
                    required={true}
                    onValueChange={onDaysInputChanged}
                />
                <HelpPortal width={200} height={50} left={-130}>
                    {tradingDaysHelp}
                </HelpPortal>
            </div>

            <div className="horizontal chart-date chart-date-to">
                <div>
                    {showFromRadios && (
                        <div
                            className="ui radio checkbox from-duration-radio until-end-date"
                            onClick={onDateRadioClicked}
                        >
                            <input
                                className="hidden"
                                type="radio"
                                readOnly={true}
                                checked={toDateRadioChecked}
                            />
                            <label>&nbsp;</label>
                        </div>
                    )}
                    <span
                        className={toDateInUse ? undefined : 'disabled-label'}
                    >
                        To Date
                    </span>
                </div>
                <DateBoundaryEditor
                    className={classNames('to-date', toDateInUse && 'in-use')}
                    placeholder={'Today'}
                    value={toDate ?? null}
                    onValueChanged={onToDateChanged}
                    showClearButton={!!(toDate && toDate.getTime() !== today)}
                    minDate={
                        fromDate
                            ? DateTime.fromJSDate(fromDate)
                                  .plus({ day: 1 })
                                  .toJSDate()
                            : undefined
                    }
                />
            </div>
        </div>
    );
}

function DateBoundaryEditor(props: {
    className?: string;
    minDate?: Date;
    maxDate?: Date;
    placeholder: string;
    value: Date | null;
    showClearButton: boolean;
    onValueChanged: (nextValue: Date | null) => void;
}) {
    const { timeFormat } = useContext(AppContext);
    const {
        className,
        minDate,
        maxDate,
        placeholder,
        showClearButton,
        value,
        onValueChanged,
    } = props;

    const onDateCleared = () => {
        onValueChanged(null);
    };

    const cssClasses = classNames('chart-date-picker', className);

    return (
        <DatePicker
            allowDeselect={true}
            className={cssClasses}
            min={minDate}
            max={maxDate}
            value={value ? value : undefined}
            onDateChange={onValueChanged}
            onDateCleared={onDateCleared}
            placeholder={placeholder}
            timeFormat={timeFormat}
            enableFutureDays={false}
            showClearButton={showClearButton}
        />
    );
}
